import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSettings } from '../context/SettingsContext';
import { usePageTitle } from '../hooks/usePageTitle';
import { useCart } from '../context/CartContext';
import api from '../api';
import CategoryNav from "../components/CategoryNav";

const Home = () => {
  const [categories, setCategories] = useState([]);
  const [productsByCategory, setProductsByCategory] = useState({});
  const { settings } = useSettings();
  const { addToCart } = useCart();
  usePageTitle();

  const safeText = (value) => {
    if (value == null) return '';
    if (typeof value === 'string' || typeof value === 'number') return String(value);
    if (typeof value === 'object') {
      return value.vi || value.name || value.title || '';
    }
    return '';
  };

  const safePhone = (value) => {
    if (value == null) return '';
    if (typeof value === 'string' || typeof value === 'number') return String(value);
    return '';
  };

  // Fetch categories
  useEffect(() => {
    api.getCategories()
      .then(data => {
        const parentMap = {};
        data.forEach(c => { parentMap[c.id] = c; });

        const parents = data.filter(c => !c.parentId);
        parents.sort((a, b) => {
          if ((a.sortOrder || 0) !== (b.sortOrder || 0)) {
            return (a.sortOrder || 0) - (b.sortOrder || 0);
          }
          return (a.name || '').localeCompare(b.name || '', 'vi');
        });

        const orderedList = [];
        parents.forEach(parent => {
          if (parent.showOnPage !== false) {
            orderedList.push({
              ...parent,
              thumbnail: parent.relatedImage || parent.thumbnail || ""
            });
          }

          const children = data.filter(c => c.parentId === parent.id);
          children.sort((a, b) => {
            if ((a.sortOrder || 0) !== (b.sortOrder || 0)) {
              return (a.sortOrder || 0) - (b.sortOrder || 0);
            }
            return (a.name || '').localeCompare(b.name || '', 'vi');
          });

          children.forEach(child => {
            if (child.showOnPage !== false) {
              orderedList.push({
                ...child,
                thumbnail: child.relatedImage || child.thumbnail || parent.relatedImage || parent.thumbnail || ""
              });
            }
          });
        });

        // Add any orphaned visible categories
        const addedIds = new Set(orderedList.map(c => c.id));
        data.forEach(c => {
          if (!addedIds.has(c.id) && c.showOnPage !== false) {
            orderedList.push({
              ...c,
              thumbnail: c.relatedImage || c.thumbnail || (c.parentId && parentMap[c.parentId] ? (parentMap[c.parentId].relatedImage || parentMap[c.parentId].thumbnail) : "") || ""
            });
          }
        });

        setCategories(orderedList);
      })
      .catch(err => {
        console.error('Failed to load categories:', err);
      });
  }, []);

  // Fetch products per category
  useEffect(() => {
    if (categories.length === 0) return;

    const fetchAll = async () => {
      const result = {};
      try {
        await Promise.all(
          categories.map(async (cat) => {
            try {
              const data = await api.getProducts({ category: cat.slug, home: true });
              const items = Array.isArray(data) ? data : (data.data || data.items || []);
              result[cat.slug] = items;
            } catch (err) {
              console.error(`Failed to load products for ${cat.slug}:`, err);
              result[cat.slug] = [];
            }
          })
        );
        setProductsByCategory(result);
      } catch (err) {
        console.error('Failed to resolve all categories products:', err);
      }
    };

    fetchAll();
  }, [categories]);

  useEffect(() => {
    const heroElements = document.querySelectorAll('.animate-hero');
    heroElements.forEach((el, index) => {
      setTimeout(() => {
        el.style.opacity = '1';
        el.style.transform = 'translateY(0)';
      }, index * 200);
    });

    const statsObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          animateStats();
          statsObserver.unobserve(entry.target);
        }
      });
    });

    const statsSection = document.querySelector('.stats');
    if (statsSection) {
      statsObserver.observe(statsSection);
    }

    return () => {
      if (statsSection) {
        statsObserver.unobserve(statsSection);
      }
    };
  }, []);

  const animateStats = () => {
    const stats = document.querySelectorAll('.stat-number');
    stats.forEach(stat => {
      const target = parseInt(stat.textContent, 10);
      let current = 0;
      const increment = target / 100;

      const timer = setInterval(() => {
        current += increment;
        if (current >= target) {
          stat.textContent = target + (stat.textContent.includes('+') ? '+' : '');
          clearInterval(timer);
        } else {
          stat.textContent = Math.floor(current);
        }
      }, 20);
    });
  };

  const getProductImage = (product) => {
    if (product.images && product.images.length > 0) {
      return api.imageUrl(product.images[0].url);
    }
    return 'https://via.placeholder.com/400x400?text=No+Image';
  };

  const getProductAlt = (product) => {
    if (product.images && product.images.length > 0 && product.images[0].altText) {
      return safeText(product.images[0].altText);
    }
    return safeText(product.name);
  };

  const formatPrice = (price) => {
    if (price == null || price === '' || Number(price) === 0) return 'Liên hệ';
    return new Intl.NumberFormat('vi-VN').format(price) + 'đ';
  };

  const zaloLink = typeof settings.zalo_url === 'string' ? settings.zalo_url : '#';
  const phoneNumber = safePhone(settings.phone);
  const shopName = safeText(settings.shop_name);

  return (
    <>
      <section id="home" className="hero">
        <div className="hero-background">
  {settings.hero_overlay_enabled !== 'false' && (
    <div className="hero-overlay"></div>
  )}
</div>

        <div className="hero-content">
          <div className="container">
            <div className="hero-text">
              <h1 className="hero-title">
                Mang vẻ đẹp thiên nhiên
                <span className="highlight"> vào cuộc sống</span>
              </h1>

              <p className="hero-description">
                Khám phá bộ sưu tập hoa tươi cao cấp được tuyển chọn kỹ lưỡng,
                thiết kế tinh tế cho những dịp đặc biệt trong cuộc sống của bạn.
              </p>

              <div className="hero-actions">
                <Link to="/products" className="btn btn-primary">Khám phá ngay</Link>
              </div>
            </div>

            <div className="hero-bubble bubble-1">
              <div className="bubble-number">500</div>
              <div className="bubble-label">Khách hàng hạnh phúc</div>
            </div>
            <div className="hero-bubble bubble-2">
              <div className="bubble-number">1000</div>
              <div className="bubble-label">Đơn hàng thành công</div>
            </div>
            <div className="hero-bubble bubble-3">
              <div className="bubble-number">5</div>
              <div className="bubble-label">Năm kinh nghiệm</div>
            </div>
            <div className="hero-bubble bubble-4">
              <div className="bubble-number">50</div>
              <div className="bubble-label">Loại hoa khác nhau</div>
            </div>
          </div>
        </div>
      </section>

     <CategoryNav />

      {categories.map(cat => {
  const products = productsByCategory[cat.slug] || [];
  if (products.length === 0) return null;

  return (
    <section key={cat.id} className="category-products-section">
      <div className="container">
        <div className="section-header">
          <h2 className="section-title">{safeText(cat.name)}</h2>
          <Link to={`/products?category=${cat.slug}`} className="section-view-all">
            Xem tất cả <i className="fas fa-arrow-right"></i>
          </Link>
        </div>

        <div className="products-grid products-grid-6">
          {products.map(product => (
            <div key={product.id} className="product-card">
              <Link 
                to={product.slug ? `/san-pham/${product.slug}` : `/products/${product.id}`} 
                className="product-image"
              >
                <img
                  src={getProductImage(product)}
                  alt={getProductAlt(product)}
                  loading="lazy"
                />
                {product.badge && <span className="product-badge">{safeText(product.badge)}</span>}
              </Link>

              <div className="product-info">
                <div className="product-name-row">
                  <h3 className="product-name">
                    <Link to={product.slug ? `/san-pham/${product.slug}` : `/products/${product.id}`} className="product-title-link">
                      {safeText(product.name)}
                    </Link>
                  </h3>
                  <button 
                    className="btn-add-to-cart-quick" 
                    onClick={(e) => {
                      e.preventDefault();
                      addToCart(product);
                    }}
                    title="Thêm vào giỏ hàng"
                  >
                    <i className="fas fa-shopping-cart"></i>
                  </button>
                </div>

                <div className="product-rating">
                  <span className="rating-value">
                    {(product.rating || 0).toFixed(1)} ★
                  </span>
                  <span className="review-count">
                    ({product.reviewCount || 0})
                  </span>
                </div>

                <div className="product-price">
                  <span className="price-current">{formatPrice(product.price)}</span>
                  {product.originalPrice && (
                    <span className="price-original">{formatPrice(product.originalPrice)}</span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
})}

      <section id="services" className="services">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Tại sao chọn chúng tôi?</h2>
          </div>

          <div className="services-grid">
            <div className="service-card">
              <div className="service-icon">
                <i className="fas fa-truck"></i>
              </div>
              <h3 className="service-title">Giao hàng nhanh chóng</h3>
              <p className="service-description">
                Giao hoa tận nơi trong vòng 2 giờ, đảm bảo độ tươi mới tuyệt đối
              </p>
            </div>

            <div className="service-card">
              <div className="service-icon">
                <i className="fas fa-paint-brush"></i>
              </div>
              <h3 className="service-title">Thiết kế theo yêu cầu</h3>
              <p className="service-description">
                Tư vấn và thiết kế hoa theo ý tưởng riêng của khách hàng
              </p>
            </div>

            <div className="service-card">
              <div className="service-icon">
                <i className="fas fa-seedling"></i>
              </div>
              <h3 className="service-title">Tư vấn chăm sóc hoa</h3>
              <p className="service-description">
                Hướng dẫn chăm sóc và bảo quản hoa để giữ được vẻ đẹp lâu nhất
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="payment-methods">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Phương thức thanh toán</h2>
          </div>

          <div className="payment-grid">
            <div className="payment-card">
              <div className="payment-icon">
                <i className="fas fa-credit-card"></i>
              </div>
              <div className="payment-info">
                <h3>Thanh toán nhanh</h3>
                <p>Nhiều hình thức thanh toán: tiền mặt, chuyển khoản, ví điện tử, thẻ tín dụng</p>
              </div>
            </div>

            <div className="payment-card">
              <div className="payment-icon">
                <i className="fas fa-file-invoice"></i>
              </div>
              <div className="payment-info">
                <h3>Hóa đơn công ty</h3>
                <p>Xuất hóa đơn VAT (+8%) cho doanh nghiệp, tổ chức</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="about" className="about">
        <div className="container">
          <div className="about-content">
            <div className="about-text">
              <span className="section-subtitle">Về {shopName}</span>
              <h2 className="section-title">Hành trình tạo nên những khoảnh khắc đẹp</h2>
              <p className="about-description">
                Với hơn 10 năm kinh nghiệm trong lĩnh vực hoa tươi, {shopName} tự hào mang đến
                những thiết kế tinh tế cùng dịch vụ chuyên nghiệp. Chúng tôi tin rằng mỗi bông hoa đều mang
                trong mình một câu chuyện và thông điệp riêng. Vì vậy, sứ mệnh của {shopName} không
                chỉ dừng lại ở việc cung cấp hoa, mà còn giúp bạn truyền tải cảm xúc một cách tinh tế và trọn vẹn nhất.
              </p>

              <div className="about-features">
                <div className="feature-item">
                  <i className="fas fa-leaf"></i>
                  <span>Hoa tươi được tuyển chọn kỹ lưỡng mỗi ngày</span>
                </div>
                <div className="feature-item">
                  <i className="fas fa-award"></i>
                  <span>Đội ngũ florist giàu kinh nghiệm, sáng tạo và am hiểu xu hướng</span>
                </div>
                <div className="feature-item">
                  <i className="fas fa-heart"></i>
                  <span>Cam kết chất lượng và sự chỉn chu trong từng chi tiết</span>
                </div>
              </div>
            </div>

            <div className="about-image">
              <img
                src="https://images.unsplash.com/photo-1487530811176-3780de880c2d?w=600&h=600&fit=crop"
                alt={`Về ${shopName}`}
              />
              <div className="about-badge">
                <span className="badge-number">10+</span>
                <span className="badge-text">Năm kinh nghiệm</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="testimonials">
        <div className="container">
          <div className="section-header">
            <span className="section-subtitle">Khách hàng nói gì</span>
            <h2 className="section-title">Những chia sẻ chân thành</h2>
          </div>

          <div className="testimonials-grid">
            <div className="testimonial-card">
              <div className="testimonial-rating">
                <i className="fas fa-star"></i>
                <i className="fas fa-star"></i>
                <i className="fas fa-star"></i>
                <i className="fas fa-star"></i>
                <i className="fas fa-star"></i>
              </div>
              <p className="testimonial-text">
                "Dịch vụ tuyệt vời! Hoa được giao đúng giờ và còn đẹp hơn cả trong hình.
                Chắc chắn sẽ quay lại {shopName} cho những dịp đặc biệt khác."
              </p>
              <div className="testimonial-author">
                <img
                  src="https://i.postimg.cc/NG6tLgCP/minh-anh.jpg"
                  alt="Nguyễn Minh Anh"
                />
                <div className="author-info">
                  <h4>Minh Anh</h4>
                  <span>Khách hàng thân thiết</span>
                </div>
              </div>
            </div>

            <div className="testimonial-card">
              <div className="testimonial-rating">
                <i className="fas fa-star"></i>
                <i className="fas fa-star"></i>
                <i className="fas fa-star"></i>
                <i className="fas fa-star"></i>
                <i className="fas fa-star"></i>
              </div>
              <p className="testimonial-text">
                "{shopName} đã trang trí đám cưới của tôi thật tuyệt vời. Mọi chi tiết đều
                hoàn hảo và đội ngũ rất chuyên nghiệp. Cảm ơn các bạn rất nhiều!"
              </p>
              <div className="testimonial-author">
                <img
                  src="https://i.postimg.cc/fR2DZBqk/duc-tran.png"
                  alt="Trần Văn Đức"
                />
                <div className="author-info">
                  <h4>Đức Trần</h4>
                  <span>Khách hàng doanh nghiệp</span>
                </div>
              </div>
            </div>

            <div className="testimonial-card">
              <div className="testimonial-rating">
                <i className="fas fa-star"></i>
                <i className="fas fa-star"></i>
                <i className="fas fa-star"></i>
                <i className="fas fa-star"></i>
                <i className="fas fa-star"></i>
              </div>
              <p className="testimonial-text">
                "Tôi đã đặt hoa online lần đầu và rất hài lòng. Website dễ sử dụng,
                hoa đẹp và giá cả hợp lý. Sẽ giới thiệu cho bạn bè!"
              </p>
              <div className="testimonial-author">
                <img
                  src="https://i.postimg.cc/BbTJ03RX/le-thi-huong.jpg"
                  alt="Lê Thị Hương"
                />
                <div className="author-info">
                  <h4>Lê Thị Hương</h4>
                  <span>Khách hàng mới</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <section className="delivery-section">
  <div className="container">
    <div className="section-header">
      <span className="section-subtitle">Hình ảnh thực tế</span>
      <h2 className="section-title">
        Một số hình ảnh bàn giao hoa cho khách
      </h2>
    </div>

    <div className="delivery-grid">
      {[
        '/ban_giao_hoa/1.jpg',
        '/ban_giao_hoa/2.jpg',
        '/ban_giao_hoa/3.jpg',
        '/ban_giao_hoa/4.jpg',
        '/ban_giao_hoa/5.jpg',
        '/ban_giao_hoa/6.jpg',
        '/ban_giao_hoa/7.jpg',
        '/ban_giao_hoa/9.jpg',
      ].map((img, index) => (
        <div key={index} className="delivery-item">
          <img src={img} alt="Bàn giao hoa" />
        </div>
      ))}
    </div>
  </div>
</section>

      <section className="cta">
        <div className="container">
          <div className="cta-content">
            <h2>Sẵn sàng tạo nên khoảnh khắc đặc biệt?</h2>
            <p>Liên hệ với chúng tôi ngay hôm nay để được tư vấn miễn phí</p>
            <div className="cta-actions">
              <a 
                href={`tel:${phoneNumber}`} 
                className="btn btn-primary"
                onClick={() => window.gtag_report_conversion && window.gtag_report_conversion(`tel:${phoneNumber}`)}
              >
                <i className="fas fa-phone"></i>
                Gọi ngay: {phoneNumber}
              </a>
              <a 
                href={zaloLink} 
                className="btn btn-outline" 
                target="_blank" 
                rel="noopener noreferrer"
                onClick={() => window.gtag_report_conversion && window.gtag_report_conversion(zaloLink)}
              >
                <img src="/images/zalo.svg" alt="Zalo" style={{ width: '24px', height: '24px', marginRight: '8px' }} />
                Chat Zalo
              </a>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Home;